import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_PLAN_YEAR } from 'lib/planYears';
import { RootState } from 'lib/store';
import { MedicareApiPlan } from 'types/api';

export interface RecommendationResults {
  hasResults: boolean;
  results: MedicareApiPlan[];
  planYear?: number;
}

export const EMPTY_RECOMMENDATION_RESULTS: RecommendationResults = {
  hasResults: false,
  results: [],
};

export interface RecommendationSlice {
  gapPlansByYear: Record<number, RecommendationResults>;
  maPlansByYear: Record<number, RecommendationResults>;
  pdpPlansByYear: Record<number, RecommendationResults>;
  maPlansAvailable: boolean;
  recommendedType: 'mapd' | 'gap_pdp' | null;
}

export const initialState: RecommendationSlice = {
  gapPlansByYear: {},
  maPlansByYear: {},
  pdpPlansByYear: {},
  maPlansAvailable: false,
  recommendedType: null,
};

export function recommendationPlanYear(payload: RecommendationResults): number {
  if (typeof payload.planYear === 'number') {
    return payload.planYear;
  }

  const fromPlan = payload.results?.find(
    (plan) => typeof plan.planYear === 'number'
  )?.planYear;

  return fromPlan ?? DEFAULT_PLAN_YEAR;
}

const storeYearResults = (
  byYear: Record<number, RecommendationResults>,
  payload: RecommendationResults
) => {
  const planYear = recommendationPlanYear(payload);
  byYear[planYear] = { ...payload, planYear };
};

const RecommendationSlice = createSlice({
  initialState,
  name: 'planSelection',
  reducers: {
    setGapPlans: (state, action) => {
      storeYearResults(state.gapPlansByYear, action.payload);
    },
    setMaPlans: (state, action) => {
      storeYearResults(state.maPlansByYear, action.payload);
    },
    setPdpPlans: (state, action) => {
      storeYearResults(state.pdpPlansByYear, action.payload);
    },
    setMaPlansAvailable: (state, action) => {
      return {
        ...state,
        maPlansAvailable: action.payload,
      };
    },
    setRecommendedType: (state, action) => {
      return {
        ...state,
        recommendedType: action.payload,
      };
    },
    resetPlans: () => {
      return initialState;
    },
  },
});

const { actions, reducer } = RecommendationSlice;

export const {
  setGapPlans,
  setMaPlans,
  setPdpPlans,
  setMaPlansAvailable,
  setRecommendedType,
  resetPlans,
} = actions;

const resultsForYear = (
  byYear: Record<number, RecommendationResults> | undefined,
  year: number
): RecommendationResults => byYear?.[year] ?? EMPTY_RECOMMENDATION_RESULTS;

export const selectRecommendationMaPlans = (
  state: RootState,
  year?: number
): RecommendationResults =>
  resultsForYear(
    state.recommendation?.maPlansByYear,
    typeof year === 'number'
      ? year
      : state.availability?.selectedPlanYear ?? DEFAULT_PLAN_YEAR
  );

export const selectRecommendationGapPlans = (
  state: RootState,
  year?: number
): RecommendationResults =>
  resultsForYear(
    state.recommendation?.gapPlansByYear,
    typeof year === 'number'
      ? year
      : state.availability?.selectedPlanYear ?? DEFAULT_PLAN_YEAR
  );

export const selectRecommendationPdpPlans = (
  state: RootState,
  year?: number
): RecommendationResults =>
  resultsForYear(
    state.recommendation?.pdpPlansByYear,
    typeof year === 'number'
      ? year
      : state.availability?.selectedPlanYear ?? DEFAULT_PLAN_YEAR
  );

export const flattenRecommendationResults = (
  byYear: Record<number, RecommendationResults> | undefined
): MedicareApiPlan[] =>
  Object.values(byYear ?? {}).flatMap((bucket) => bucket.results ?? []);

export const selectMaPlansAvailable = (state: RootState) =>
  state.recommendation.maPlansAvailable;

export const selectRecommendedType = (state: RootState) =>
  state.recommendation.recommendedType;

export default reducer;
